import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js'

export interface AuthAdapter {
  getSession(): Promise<Session | null>
  sendMagicLink(email: string, redirectTo: string): Promise<void>
  verifyOtp(email: string, token: string): Promise<Session>
  refreshSession(): Promise<Session | null>
  signOut(): Promise<void>
  onSessionChange(callback: (session: Session | null) => void): () => void
}

let client: SupabaseClient | undefined

function supabase(): SupabaseClient {
  if (client) return client
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
  if (!url || !key) {
    throw new Error('Supabase Auth is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.')
  }
  client = createClient(url, key, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  })
  return client
}

export const supabaseAuthAdapter: AuthAdapter = {
  async getSession() {
    const { data, error } = await supabase().auth.getSession()
    if (error) throw error
    return data.session
  },
  async sendMagicLink(email, redirectTo) {
    const { error } = await supabase().auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
    })
    if (error) throw error
  },
  async verifyOtp(email, token) {
    const { data, error } = await supabase().auth.verifyOtp({ email, token, type: 'email' })
    if (error) throw error
    if (!data.session) throw new Error('The code was accepted but no session was created.')
    return data.session
  },
  async refreshSession() {
    const { data, error } = await supabase().auth.refreshSession()
    if (error) throw error
    return data.session
  },
  async signOut() {
    const { error } = await supabase().auth.signOut()
    if (error) throw error
  },
  onSessionChange(callback) {
    const { data } = supabase().auth.onAuthStateChange((_event, session) => callback(session))
    return () => data.subscription.unsubscribe()
  },
}
